import React, { useEffect, useMemo } from "react";
import LoadingState from "./components/general/LoadingState";
import ErrorState from "./components/general/ErrorState";
import CompanyInfo from "./components/general/CompanyInfo";
import {
  fetchCompanyData,
  fetchTicketData as fetchTicket,
} from "./components/general/AppHelpers";
import {
  useTicket,
  setTicketLoading,
  setTicketError,
  useConfig,
  setConfig,
  useCompany,
  setCompanyLoading,
  isCompanyFetched,
} from "./store/Store";
import LanguageSelector from "./components/general/LanguageSelector";
import ModalManager from "./components/general/ModalManager";
import Ticket from "./components/ticket/Ticket";
import PaymentSummary from "./components/payment/components/PaymentSummary";
import PaymentProcessor from "./components/payment/components/PaymentProcessor";
import { MyTimeLogoIcon } from "./components/general/Icons";
import { useTranslation } from "react-i18next";
import { LockerIcon } from "./components/general/Icons";
import PaymentHistory from "./components/payment/components/PaymentHistory";

function App() {
  const {
    loading: ticketLoading,
    error: ticketError,
    data: ticketData,
  } = useTicket();
  const config = useConfig();
  const { t } = useTranslation();
  const { loading: companyLoading, error: companyError } = useCompany();

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get("access_token");
      const color = params.get("color") || "#3B82F6"; // Default blue-500 color
      const textColor = params.get("text_color") || "#111827"; // Default gray-900 color
      const font = params.get("font") || "";
      const debug = params.get("debug") === "true";

      if (!accessToken) {
        setTicketError("Missing required access_token parameter");
        setTicketLoading(false);
        return;
      }

      setConfig({ accessToken, color, textColor, font, debug });
      fetchTicket(accessToken);
    } catch (error) {
      console.error("Error parsing URL parameters:", error);
      setTicketError("Error parsing URL parameters");
      setTicketLoading(false);
    }
  }, []);

  const companyId = useMemo(
    () => ticketData?.resource?.company?.id,
    [ticketData]
  );

  useEffect(() => {
    if (companyId && !isCompanyFetched(companyId)) {
      setCompanyLoading(true);
      fetchCompanyData(companyId);
    }
  }, [companyId]);

  // Create a style object with dynamic font if provided
  const dynamicStyles = {};
  if (config.font) {
    dynamicStyles.fontFamily = config.font;
  }

  if (ticketLoading || companyLoading) {
    return <LoadingState dynamicStyles={dynamicStyles} />;
  }

  if (ticketError || companyError) {
    return (
      <ErrorState
        error={ticketError || companyError}
        dynamicStyles={dynamicStyles}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" style={dynamicStyles}>
      <div className="max-w-7xl mx-auto pt-3 pb-8 px-2 sm:px-6 lg:px-8">
        <div className="flex justify-end mb-4">
          <LanguageSelector />
        </div>
        <div className="bg-white rounded-xl overflow-hidden min-h-[600px]">
          <CompanyInfo />
          <div className="flex flex-col min-[920px]:flex-row min-h-[600px]">
            <Ticket />
            <div className="w-full basis-2/5 border-t md:border-t-0 md:border-l border-gray-200 p-4 sm:p-8 rounded-lg flex flex-col justify-between">
              <div>
                <PaymentSummary />
                <PaymentHistory />
                <h2 className="text-lg font-medium text-black mt-10 mb-2">
                  {t("reviewPayment")}
                </h2>
                <div className="text-xs text-black-light flex items-center gap-1">
                  <LockerIcon /> {t("paymentSummary.securedTransactions")}
                </div>
                <PaymentProcessor />
              </div>
              <MyTimeLogoIcon />
            </div>
          </div>
        </div>
      </div>
      {<ModalManager />}
    </div>
  );
}

export default App;
